import sum from 'lodash/sum';
import { selectEventConfig } from 'models/virtual_event';
import { createSelector } from 'reselect';
import { RootState } from 'store';
import { TwilioPreloadSliceType } from './preload';
import {
  ConversationType,
  metadataAdapter,
  ConversationMetadata,
  userAdapter,
  TwilioSliceType,
  ConnectionState,
  GroupConversationMetadata,
  PublicConversationMetadata,
  TwilioUser,
} from './types';
import { calculateUnread } from './utils';

/**
 * @description: Sort by role, tag, name
 */
const userSortFn = (prev: TwilioUser, next: TwilioUser) => {
  const roleValueMap = {
    organizer: 0,
    exhibitor: 1,
    attendee: 2,
  };

  const prevRoleValue = roleValueMap[prev.role] ?? 2;
  const nextRoleValue = roleValueMap[next.role] ?? 2;
  if (prevRoleValue !== nextRoleValue) return prevRoleValue - nextRoleValue;

  // Cast to Number to do number operations
  const prevTag = Number(!prev.tag);
  const nextTag = Number(!next.tag);
  if (prevTag !== nextTag) return prevTag - nextTag;

  return prev.name.localeCompare(next.name);
};

export const selectTwilioState = (state: RootState) => state.twilio as TwilioSliceType;
export const selectTwilioPreloadState = (state: RootState) => state.twilioPreload as TwilioPreloadSliceType;

export const selectTwilioToken = createSelector(
  selectTwilioPreloadState,
  (preloadSlice) => preloadSlice.jwtToken,
);

export const selectNotificationSettings = createSelector(
  selectTwilioPreloadState,
  (preloadSlice) => preloadSlice.notificationSettings,
);

export const selectTwilioListCollapseState = (type: ConversationType) => createSelector(
  selectTwilioPreloadState,
  (preloadSlice) => preloadSlice.collapseStates[type] ?? false,
);

export const isTwilioFullscreen = createSelector(
  selectTwilioPreloadState,
  (preloadSlice) => preloadSlice.fullscreen,
);

export const selectTwilioClient = createSelector(
  selectTwilioState,
  (twilioSlice) => twilioSlice.client,
);

export const selectConnectionState = createSelector(
  selectTwilioState,
  (twilioSlice) => twilioSlice.connectionState,
);

export const isTwilioConnected = createSelector(
  selectConnectionState,
  (connectionState) => connectionState === ConnectionState.CONNECTED,
);

export const selectChatInterfaceState = createSelector(
  selectTwilioState,
  (twilioSlice) => twilioSlice.chatInterfaceActive,
);

export const {
  selectById: selectUserByIdFn,
  selectAll: selectAllUsers,
  selectEntities: selectTwilioUserEntities,
} = userAdapter.getSelectors((state: any) => (state.twilio as TwilioSliceType).users);

export const {
  selectAll: selectAllMetadata,
  selectById: selectMetadataByIdFn,
} = metadataAdapter.getSelectors((state: any) => (state.twilio as TwilioSliceType).metadata);

export const selectTwilioMetdata = (type: ConversationType) => createSelector(
  selectAllMetadata,
  (allMetadata) => allMetadata
    .filter(({ conversationType }) => conversationType === type)
    .sort((prevMetadata, nextMetadata) => {
      const prevDate = prevMetadata.lastMessage?.dateCreated || prevMetadata.dateCreated;
      const nextDate = nextMetadata.lastMessage?.dateCreated || nextMetadata.dateCreated;
      return nextDate.getTime() - prevDate.getTime();
    }),
);

export const selectPrivateMetadataId = createSelector(
  selectTwilioMetdata(ConversationType.PRIVATE),
  (privateMetadata) => privateMetadata.map((metadata) => metadata.id),
);

export const selectGroupMetadataId = createSelector(
  selectTwilioMetdata(ConversationType.GROUP),
  (groupMetadata) => groupMetadata.map((metadata) => metadata.id),
);

export const selectPublicMetadataId = createSelector(
  selectTwilioMetdata(ConversationType.PUBLIC),
  (groupMetadata) => groupMetadata.map((metadata) => metadata.id),
);

export const selectRelevantMetdataCount = createSelector(
  selectAllMetadata,
  (allMetadata) => allMetadata
    .filter(({ conversationType }) => [ConversationType.ANNOUNCEMENT, ConversationType.PRIVATE, ConversationType.GROUP, ConversationType.PUBLIC].includes(conversationType))
    .length,
);

export const selectRelevantMetdataCounts = createSelector(
  selectAllMetadata,
  (allMetadata) => {
    const filterTarget = (type: ConversationType) => allMetadata.filter((metadata) => metadata.conversationType === type);
    return Object.values(ConversationType).reduce((counts, type) => {
      counts[type] = filterTarget(type).length;
      return counts;
    }, {} as Record<ConversationType, number>);
  },
);

export const selectAnnouncementMetadata = createSelector(
  selectAllMetadata,
  (allMetadata) => allMetadata
    .find(({ conversationType }) => (conversationType === ConversationType.ANNOUNCEMENT)) as ConversationMetadata,
);

export const selectCurrentSelectedChat = createSelector(
  selectTwilioState,
  (twilioSlice) => twilioSlice.selectedChat,
);

export const getAllMessagesByChatId = (id: string) => createSelector(
  selectTwilioState,
  (twilioSlice) => Object.values(twilioSlice.messages[id]?.entities || {})
    .sort((prevMessage, nextMessage) => prevMessage.index - nextMessage.index),
);

export const selectMetadataById = (id: string) => createSelector(
  (state: any) => selectMetadataByIdFn(state, id),
  (metadata) => metadata || null,
);

export const selectMetadataOtherUserById = (id: string, exhibitorOnly: boolean) => createSelector(
  [selectMetadataById(id), selectTwilioUserEntities],
  (metaData: GroupConversationMetadata | PublicConversationMetadata, twilioUserEntities) => (
    metaData?.otherUserIds.map((userId) => twilioUserEntities[userId])
      .filter((user) => (exhibitorOnly
        ? user?.exhibitorBoothId === metaData.uniqueName
        : Boolean(user)))
      .sort(userSortFn)
  ),
);

export const selectUserById = (id: string) => createSelector(
  (state: any) => selectUserByIdFn(state, id),
  (participant) => participant || null,
);

export const selectTotalUnreadCount = createSelector(
  [selectAllMetadata, selectEventConfig],
  (metadatas, eventConfig) => {
    const totalUnread = sum(metadatas.map((metadata) => calculateUnread(metadata, eventConfig)));
    return totalUnread;
  },
);

export const selectUnreadCountById = (id: string) => createSelector(
  [selectMetadataById(id), selectEventConfig],
  (metadata, eventConfig) => calculateUnread(metadata, eventConfig),
);

export const selectChatAlert = createSelector(
  selectTwilioState,
  (twilioSlice) => twilioSlice.alert,
);

export const selectImageViewerInitial = createSelector(
  selectTwilioState,
  (twilioSlice) => twilioSlice.imageViewerInitial,
);

export const haveAllConversationsLoaded = createSelector(
  selectTwilioState,
  (twilioSlice) => twilioSlice.allConversationsLoaded,
);
